/** Stable expiry sibling. Money, receipts, PI and browser intents are not owned here. */
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { isApiError, sameModuleOfferLocator as sameId } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { sameCompanyBillingScope } from "@bw/shared/utils";
import type { CompanyExpiryAttempt, CompanyExpiryCommand, ModuleAgreementExpiryProps } from "./companyExpiryContracts";
import { useCompanyExpiryReview, useCompleteCompanyExpiry, companyExpiryDenial, expiryCommand,
  sameExpiryCommand, sameExpirySelection } from "./useModuleExpiry";
import { ModuleExpiryReview } from "./ModuleExpiryReview";

export function ModuleAgreementExpiry({ copy, selection, expiryFenceRevision, onExpiryDenied, onExpiryRevalidated }: ModuleAgreementExpiryProps) {
  const { user } = useAuth();
  const scope = useMemo(() => ({ actorId: user?.uid ?? "", orgId: copy.org_id, acceptanceId: copy.acceptance_id,
    environment: copy.offer.environment }), [user?.uid, copy.org_id, copy.acceptance_id, copy.offer.environment]);
  const selectionId = JSON.stringify(selection), scopeId = JSON.stringify(scope);
  const id = JSON.stringify([scopeId, selectionId, expiryFenceRevision, copy.offer.module_key]);
  const live = useRef({ id, active: true, scope, token: Symbol() });
  if (live.current.id !== id) live.current = { id, scope, active: live.current.active, token: Symbol() };
  const current = (token: symbol) => live.current.active && live.current.id === id && live.current.token === token
    && sameCompanyBillingScope(live.current.scope, scope);
  const [acknowledgedFence, setAcknowledgedFence] = useState(0), [denied, setDenied] = useState(false);
  const deniedRef = useRef(false), seen = useRef(new Set<Error>());
  const owner = useRef<{ token: symbol; revision: number } | null>(null), [busy, setBusy] = useState(false);
  const previousFence = useRef(expiryFenceRevision);
  const attempt = useRef<CompanyExpiryAttempt | null>(null);
  // Only invocation metadata is local. The server result remains in mutation Query.
  const [resultInvocation, setResultInvocation] = useState<{ id: string; token: symbol; commandId: string } | null>(null);
  const [explicitReview, setExplicitReview] = useState<{ id: string; request: CompanyExpiryCommand } | null>(null);
  const [notice, setNotice] = useState<{ id: string; text: string } | null>(null);
  const [reviewRequired, setReviewRequired] = useState<string | null>(null);
  const suspended = denied || expiryFenceRevision !== acknowledgedFence;
  const review = useCompanyExpiryReview(scope, copy.offer.module_key, expiryFenceRevision, suspended, selection);
  const complete = useCompleteCompanyExpiry(scope, copy.offer.module_key, expiryFenceRevision, suspended, selection);
  const errors = [review.error, complete.error];
  const losses = errors.flatMap(error => {
    const event = companyExpiryDenial(error);
    return error && !seen.current.has(error) && event && event.expiryFenceRevision === expiryFenceRevision && selection !== null
      && sameId(event.purchaseId, selection.purchase_id) && sameCompanyBillingScope(event.scope, scope) ? [{ error, event }] : [];
  });
  // Conceal and invalidate old controller effects in this render, before a
  // deferred response can benefit from a later explicit access acknowledgement.
  if (losses.length && !deniedRef.current) { deniedRef.current = true; live.current.token = Symbol(); }
  useLayoutEffect(() => { live.current.active = true; return () => { live.current.active = false; live.current.token = Symbol(); }; }, [scopeId]);
  useLayoutEffect(() => {
    if (previousFence.current !== expiryFenceRevision) {
      previousFence.current = expiryFenceRevision;
      // Retire ONLY the old epoch's UI owner/observer, not its HTTP work. Its
      // original invocation lease stays dead, and its finally cannot own a new GET.
      if (owner.current?.revision !== expiryFenceRevision) { owner.current = null; setBusy(false); }
      complete.reset(); setResultInvocation(null); setExplicitReview(null);
    }
    if (expiryFenceRevision !== acknowledgedFence) { deniedRef.current = true; setDenied(true); setResultInvocation(null); }
  }, [expiryFenceRevision, acknowledgedFence, complete.reset]);
  useLayoutEffect(() => {
    for (const { error, event } of losses) {
      seen.current.add(error); deniedRef.current = true; setDenied(true); setResultInvocation(null); setExplicitReview(null); onExpiryDenied(event);
    }
  });

  async function refresh() {
    if (selection === null || owner.current !== null || review.isFetching) return;
    const token = live.current.token, owned = { token: Symbol(), revision: expiryFenceRevision }; owner.current = owned; setBusy(true);
    try {
      const result = await review.refetch();
      if (!current(token) || !result.isSuccess) return;
      deniedRef.current = false; setDenied(false); setAcknowledgedFence(expiryFenceRevision); setResultInvocation(null); setExplicitReview(null); setNotice(null);
      onExpiryRevalidated({ scope, purchaseId: selection.purchase_id, expiryFenceRevision, operation: "getExpiryReview" });
      const held = attempt.current;
      if (held !== null) {
        if (!sameCompanyBillingScope(held.scope, scope) || !sameExpirySelection(held.original, selection)) {
          setReviewRequired(id); setNotice({ id, text: "A previous module-end request is unresolved. Review that original before confirming another." }); return;
        }
        if (result.data.state === "applied") {
          attempt.current = null;
          if (!sameId(result.data.command_id, held.request.command_id) || result.data.review_fingerprint !== held.request.reviewed_fingerprint)
            setNotice({ id, text: "This original module is already ended. The earlier request's specific outcome remains unconfirmed." });
        } else if (result.data.state === "eligible") {
          const fresh = expiryCommand(result.data);
          if (sameExpiryCommand(fresh, held.request)) {
            // New local invocation lease, SAME six fields, never old effects.
            attempt.current = Object.freeze({ ...held, expiryFenceRevision });
          } else {
            // The held attempt is NOT retired by GET/fingerprint change. Keep
            // the new exact server command pending the user's explicit choice.
            setExplicitReview({ id, request: fresh });
            setNotice({ id, text: "The earlier request's specific outcome is unconfirmed. You can explicitly confirm this new server review for the same original." });
          }
        } else {
          setReviewRequired(id); setNotice({ id, text: "The original request is not confirmed by this review. No replacement request was created." }); return;
        }
      }
      setReviewRequired(null);
    } finally {
      if (owner.current === owned) { owner.current = null; if (live.current.active && sameCompanyBillingScope(live.current.scope, scope)) setBusy(false); }
    }
  }
  async function confirm() {
    const token = live.current.token;
    if (!current(token) || deniedRef.current || suspended || owner.current !== null || review.isFetching || selection === null
      || reviewRequired === id || !review.isSuccess || !review.data.can_confirm || review.data.state !== "eligible") return;
    const command = expiryCommand(review.data), held = attempt.current;
    const offered = explicitReview?.id === id ? explicitReview.request : null;
    const acceptFresh = offered !== null && sameExpiryCommand(offered, command);
    if (held !== null && (!sameCompanyBillingScope(held.scope, scope) || !sameExpirySelection(held.original, selection)
      || !sameExpiryCommand(held.request, command) && !acceptFresh)) {
      setNotice({ id, text: "Review the unresolved original request before another confirmation." }); setReviewRequired(id); return;
    }
    const captured = Object.freeze({ scope: Object.freeze({ ...scope }), expiryFenceRevision,
      original: Object.freeze({ ...selection }), request: offered !== null && acceptFresh ? offered : held?.request ?? command });
    // This explicit click alone retires the held unknown command in favour of
    // the validated server command. It makes no claim about the old outcome.
    attempt.current = captured;
    const owned = { token: Symbol(), revision: expiryFenceRevision }; owner.current = owned;
    setBusy(true); setNotice(null); setExplicitReview(null); setResultInvocation(null);
    try {
      const result = await complete.mutateAsync(captured);
      if (!current(token) || deniedRef.current) return;
      setResultInvocation({ id, token, commandId: captured.request.command_id }); attempt.current = null;
      if (result.state === "blocked_unknown") setReviewRequired(id);
    } catch (error) {
      if (!current(token) || deniedRef.current) return;
      setReviewRequired(id);
      if (isApiError(error) && error.status === 409) {
        attempt.current = null; setExplicitReview(null);
        setNotice({ id, text: "The reviewed facts changed. Refresh and explicitly confirm the new review; no request was resubmitted." });
      } else setNotice({ id, text: "The original module-end request outcome is unknown. Refresh to check it before any exact retry. No replacement was created." });
    } finally {
      if (owner.current === owned) { owner.current = null; if (live.current.active && sameCompanyBillingScope(live.current.scope, scope)) setBusy(false); }
    }
  }

  // Hooks always remain mounted; selection does not tear down agreement scopes.
  if (selection === null || !sameId(selection.acceptance_id, scope.acceptanceId)) return null;
  const inFlight = busy || complete.isPending || review.isFetching;
  if (denied || losses.length > 0 || expiryFenceRevision !== acknowledgedFence) return <Alert severity="warning">
    Module-end review access is unavailable. Other agreement records have separate access.
    <Button size="small" disabled={inFlight} onClick={() => { void refresh(); }}>Recheck module end access</Button>
  </Alert>;
  const result = complete.isSuccess && complete.variables !== undefined && resultInvocation !== null
    && resultInvocation.id === id && resultInvocation.token === live.current.token
    && sameId(resultInvocation.commandId, complete.data.command_id) && complete.variables.expiryFenceRevision === expiryFenceRevision
    && sameCompanyBillingScope(complete.variables.scope, scope) && sameExpirySelection(complete.variables.original, selection)
    && sameId(complete.variables.request.command_id, complete.data.command_id) ? complete.data : null;
  const offered = explicitReview?.id === id ? explicitReview.request : null;
  const changedOffer = offered !== null && (!review.isSuccess || review.data.state !== "eligible"
    || !sameExpiryCommand(offered, expiryCommand(review.data)));
  return <Stack spacing={1.5} aria-label="Original module end">
    <Typography variant="body2">For purchase created {new Date(selection.created_at).toLocaleString()}</Typography>
    {notice?.id === id ? <Alert severity="warning">{notice.text}</Alert> : null}
    {review.isPending ? <Typography role="status">Loading module-end review…</Typography> : null}
    {review.isError ? <Alert severity="warning">Module-end information is unavailable.
      <Button size="small" disabled={inFlight} onClick={() => { void refresh(); }}>Refresh module end review</Button>
    </Alert> : null}
    {review.isSuccess ? <ModuleExpiryReview review={review.data} result={result} busy={inFlight}
      confirmDisabled={suspended || reviewRequired === id || changedOffer || attempt.current !== null && !sameExpirySelection(attempt.current.original, selection)}
      onConfirm={() => { void confirm(); }} onRefresh={() => { void refresh(); }} /> : null}
  </Stack>;
}
